import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";

// Generic create/edit modal for PO / PSO / PEO. The parent tab provides the
// async submit handler so this form stays unaware of which service to call.

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const Field = ({ label, htmlFor, hint, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5"
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const OutcomeForm = ({ type, outcome, onSubmit, onClose }) => {
  const isEdit = !!outcome;
  const showCategory = type === "PO";

  const [form, setForm] = useState({
    code: outcome?.code || "",
    statement: outcome?.statement || "",
    category: outcome?.category || "",
    order: outcome?.order ?? 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const setField = (key, value) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const payload = {
      code: form.code.trim().toUpperCase(),
      statement: form.statement.trim(),
      order: Number(form.order) || 0,
    };
    if (showCategory) payload.category = form.category.trim();

    if (!payload.code || !payload.statement) {
      setError("Code and statement are required.");
      return;
    }
    if (!/^[A-Z0-9]{2,10}$/.test(payload.code)) {
      setError("Code must be 2-10 chars: A-Z, 0-9 only.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(payload);
      toast.success(isEdit ? `${type} updated` : `${type} created`);
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>
                {isEdit ? `Edit ${type}` : `New ${type}`}
              </div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                {isEdit ? outcome.code : `Add a new ${type}`}
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
            </div>
            <button
              onClick={() => onClose(false)}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-5 overflow-y-auto"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Code *" htmlFor="code" hint={`A-Z, 0-9. Example: ${type}1`}>
                <input
                  id="code"
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setField("code", e.target.value.toUpperCase())
                  }
                  placeholder={`${type}1`}
                  required
                  className={inputBase}
                />
              </Field>
              <Field
                label="Display Order"
                htmlFor="order"
                hint="Used to sort in lists"
              >
                <input
                  id="order"
                  type="number"
                  min={0}
                  value={form.order}
                  onChange={(e) => setField("order", e.target.value)}
                  className={inputBase}
                />
              </Field>
              {showCategory && (
                <div className="sm:col-span-2">
                  <Field
                    label="Category"
                    htmlFor="category"
                    hint="e.g. Engineering Knowledge, Problem Analysis"
                  >
                    <input
                      id="category"
                      type="text"
                      value={form.category}
                      onChange={(e) => setField("category", e.target.value)}
                      placeholder="e.g. Engineering Knowledge"
                      className={inputBase}
                    />
                  </Field>
                </div>
              )}
            </div>

            <Field label="Statement *" htmlFor="statement">
              <textarea
                id="statement"
                value={form.statement}
                onChange={(e) => setField("statement", e.target.value)}
                rows={5}
                placeholder={`Full ${type} statement…`}
                required
                className={cn(inputBase, "resize-y")}
              />
            </Field>

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                  submitting
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                {submitting ? "Saving…" : isEdit ? "Save changes" : `Create ${type}`}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OutcomeForm;
